import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { AppRoutes } from './AppRoutes';
import { initObservability } from '@/app/utils/observability';
import { initInstallPrompt } from '@/app/utils/installPrompt';
import { initAppUpdates } from '@/app/utils/appUpdate';
import { coverScreenWhenInstalled } from '@/app/utils/installedViewport';
import '@/index.css'; // Importing global styles for the app

// Clerk publishable key — required by ClerkProvider, which THROWS without it.
//
// The old version warned "auth and subscription features will be unavailable"
// and then passed the missing value straight through. That reads like graceful
// degradation and is not: ClerkProvider throws during render, React unmounts
// the tree, and #root is left empty. Every visitor got a white screen,
// including the "Try the demo" path that never touches auth (JAR-971).
//
// Production builds cannot reach this state any more — vite.config.ts refuses
// to build without a valid key. What is left is local development with no
// .env, and the answer there is to say so ON THE PAGE rather than render
// nothing and leave a console warning nobody has open.
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const rootElement = document.getElementById('root')!;

function boot(): void {
  // Initialise error reporting before anything renders (Sentry et al. wire in here).
  initObservability();

  // Capture `beforeinstallprompt` from the very start — Chrome fires it early,
  // often before the user has signed in, and it is a one-shot event (JAR-2031).
  initInstallPrompt();

  // From the Home Screen the app covers the whole screen, and a browser tab
  // doesn't (JAR-2496).
  coverScreenWhenInstalled();

  // Register the service worker, and look for a deploy made while the app is
  // open, so a tab left open doesn't keep running the old build (JAR-2323).
  initAppUpdates();

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ClerkProvider>
    </React.StrictMode>
  );
}

// An if/else rather than an early throw. A module-scope throw fires before
// React mounts, so the ErrorBoundary cannot catch it and the white screen is
// back by another route — the same mistake this file already made once with
// the API base (core#179 review, H1).
if (!CLERK_PUBLISHABLE_KEY) {
  console.error(
    '[Clerk] VITE_CLERK_PUBLISHABLE_KEY is not set, so the app cannot start. ' +
      'Set it in .env (see .env.example).'
  );
  // Inline styles on purpose: this has to render even if the stylesheet is the
  // thing that failed, and it is the one screen that must never depend on
  // anything else working.
  ReactDOM.createRoot(rootElement).render(
    <div
      role="alert"
      style={{
        font: '16px/1.6 system-ui, -apple-system, sans-serif',
        maxWidth: '34rem',
        margin: '4rem auto',
        padding: '0 1.5rem',
        color: '#1F2421',
      }}
    >
      <h1 style={{ font: '600 20px/1.3 system-ui, -apple-system, sans-serif', margin: '0 0 0.75rem' }}>
        This build is missing its configuration
      </h1>
      <p style={{ margin: '0 0 0.75rem' }}>
        <code>VITE_CLERK_PUBLISHABLE_KEY</code> is not set, so the app cannot start.
      </p>
      <p style={{ margin: 0 }}>
        Copy <code>.env.example</code> to <code>.env</code> and add the publishable key from the
        Clerk dashboard, then restart the dev server.
      </p>
    </div>
  );
} else {
  boot();
}
